import type { CaptionStyleConfig, SceneChunk, SceneFace, SceneMetadata } from "../schemas"
import { resolveCaptionCenter } from "@/lib/caption-layout"
import {
  faceAt,
  faceCenterXAt,
  faceCropRect,
  keyframeAt,
  layoutChunkAt,
  layoutPanelRects,
  resolveLayoutSpans,
  singleCropRect,
} from "./layout"

/**
 * Face-aware placement for text boxes (the auto hook, text headings): where a full-width box can sit
 * without covering anyone's face. Pure: the composition, the editor preview and the clip cards share it.
 */

/** A vertical band of the output frame, as fractions of its height (y0 above y1). */
export interface Band {
  y0: number
  y1: number
}

/** A band that only exists between two clip times (another text box, the captions). */
export interface TimedBand extends Band {
  startSec: number
  endSec: number
}

/** From `startSec` on, the box's centre sits `y` (fraction of the frame height) down the frame. */
export interface Placement {
  startSec: number
  y: number
}

/** The text box's centre where nothing is known about the frame (the old fixed "top" spot). */
export const DEFAULT_TEXT_Y = 0.2
/** Kept clear above a face (hair, forehead) and below it (chin), as shares of the face height. */
const FACE_MARGIN_TOP = 0.35
const FACE_MARGIN_BOTTOM = 0.2
/** Gap between the box and anything it avoids, and the frame edges it never crosses. */
const GAP = 0.01
const EDGE = 0.03
const STEP_SEC = 0.25
const SCAN_STEP = 0.005
/** A move between placements eases over this long. */
export const PLACEMENT_EASE_SEC = 0.3

function overlaps(a: Band, b: Band): boolean {
  return a.y0 < b.y1 && b.y0 < a.y1
}

/** The people a chunk shows at `t`, in source fractions (its tracked faces, else its single face). */
function facesAt(chunk: SceneChunk, t: number): SceneFace[] {
  if (chunk.faces?.length) return chunk.faces.map((f) => faceAt(f, t))
  if (chunk.face_track?.length) return [keyframeAt(chunk.face_track, t)]
  if (chunk.face_count > 0) return [{ x_pct: chunk.face_center_x_pct ?? 0.5, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }]
  return []
}

/** The band the fitted source video covers in "wide" (the blurred bars above and below are free). */
function videoBand(width: number, height: number, source: SceneMetadata["source"]): Band {
  const h = (width / source.width) * source.height / height
  return { y0: Math.max(0, (1 - h) / 2), y1: Math.min(1, (1 + h) / 2) }
}

/** The output bands covered by faces (with hair and chin margins) when `chunk` is drawn at `t`. */
export function faceBandsAt(chunk: SceneChunk, t: number, width: number, height: number, source: SceneMetadata["source"]): Band[] {
  const faces = facesAt(chunk, t)
  const aspect = width / height
  const sourceBand = (f: SceneFace) => ({
    x0: (f.x_pct - f.w_pct / 2) * source.width,
    x1: (f.x_pct + f.w_pct / 2) * source.width,
    y0: (f.y_pct - f.h_pct * (0.5 + FACE_MARGIN_TOP)) * source.height,
    y1: (f.y_pct + f.h_pct * (0.5 + FACE_MARGIN_BOTTOM)) * source.height,
  })
  switch (chunk.mode) {
    case "wide":
      return [videoBand(width, height, source)]
    case "screenshare": {
      const [pip] = layoutPanelRects(chunk, aspect)
      return [videoBand(width, height, source), { y0: pip.y, y1: pip.y + pip.h }]
    }
    case "single": {
      const crop = singleCropRect(faceCenterXAt(chunk, t), aspect, source)
      return faces
        .map(sourceBand)
        .filter((b) => b.x1 > crop.x && b.x0 < crop.x + crop.w)
        .map((b) => ({ y0: b.y0 / source.height, y1: b.y1 / source.height }))
    }
    default: {
      const bands: Band[] = []
      for (const rect of layoutPanelRects(chunk, aspect)) {
        const panelFace = chunk.faces?.[rect.faceIndex] ?? { x_pct: 0.5, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }
        const crop = faceCropRect(faceAt(panelFace, t), rect.w * width, rect.h * height, source)
        for (const b of faces.map(sourceBand)) {
          if (b.x1 <= crop.x || b.x0 >= crop.x + crop.w || b.y1 <= crop.y || b.y0 >= crop.y + crop.h) continue
          const toPanel = (y: number) => rect.y + rect.h * Math.max(0, Math.min(1, (y - crop.y) / crop.h))
          bands.push({ y0: toPanel(b.y0), y1: toPanel(b.y1) })
        }
      }
      return bands
    }
  }
}

/**
 * Where a layout wants a text box of `boxHeight`: on the seam of a split (between the stacked panels),
 * in the top blurred bar of a wide frame, and in a single crop above the head when there is room,
 * else just below the chin.
 */
export function preferredTextY(chunk: SceneChunk | null, faceBands: Band[], boxHeight: number, width: number, height: number, source: SceneMetadata["source"]): number {
  if (!chunk) return DEFAULT_TEXT_Y
  switch (chunk.mode) {
    case "dual":
    case "triple":
    case "quad":
      return 0.5
    case "wide":
    case "screenshare": {
      const video = videoBand(width, height, source)
      return video.y0 >= boxHeight + 2 * GAP ? video.y0 / 2 : video.y1 + (1 - video.y1) / 2
    }
    case "single": {
      if (!faceBands.length) return DEFAULT_TEXT_Y
      const top = Math.min(...faceBands.map((b) => b.y0))
      const bottom = Math.max(...faceBands.map((b) => b.y1))
      if (top - GAP - EDGE >= boxHeight) return (EDGE + top - GAP) / 2
      return bottom + GAP + boxHeight / 2
    }
  }
}

export interface TextPlacementInput {
  meta: SceneMetadata | undefined
  fps: number
  totalFrames: number
  width: number
  height: number
  startSec: number
  endSec: number
  /** The box height as a fraction of the frame height. */
  boxHeight: number
  /** Other things on screen the box must not cover (captions, earlier text boxes). */
  avoid?: TimedBand[]
}

/**
 * Where a text box shown from startSec to endSec goes: one spot clear of every face (and of `avoid`)
 * for the whole time when there is one, nearest the first layout's preferred spot; otherwise one spot
 * per layout span, each eased into (PLACEMENT_EASE_SEC). With no scene data, the default spot.
 */
export function placeTextBox(input: TextPlacementInput): Placement[] {
  const { meta, fps, totalFrames, width, height, startSec, endSec, boxHeight, avoid = [] } = input
  if (!meta?.chunks?.length) return [{ startSec, y: DEFAULT_TEXT_Y }]
  const spans = resolveLayoutSpans(meta, fps, totalFrames)
  const times: number[] = []
  for (let t = startSec; t < endSec; t += STEP_SEC) times.push(t)
  if (!times.length) times.push(startSec)

  const samples = times.map((t) => {
    const chunk = layoutChunkAt(spans, Math.min(totalFrames - 1, Math.round(t * fps)))
    const faces = chunk ? faceBandsAt(chunk, t, width, height, meta.source) : []
    const others = avoid.filter((b) => b.startSec <= t && t < b.endSec)
    return { t, chunk, faces, blocked: [...faces, ...others], preferred: preferredTextY(chunk, faces, boxHeight, width, height, meta.source) }
  })

  const lo = EDGE + boxHeight / 2
  const hi = 1 - EDGE - boxHeight / 2
  const candidates = (preferred: number) => {
    const ys = [Math.max(lo, Math.min(hi, preferred))]
    for (let y = lo; y <= hi; y += SCAN_STEP) ys.push(y)
    return ys.sort((a, b) => Math.abs(a - preferred) - Math.abs(b - preferred))
  }
  const clashes = (y: number, group: typeof samples) => {
    const box = { y0: y - boxHeight / 2 - GAP, y1: y + boxHeight / 2 + GAP }
    return group.filter((s) => s.blocked.some((b) => overlaps(box, b))).length
  }
  const best = (group: typeof samples) => {
    const ys = candidates(group[0].preferred)
    return ys.find((y) => clashes(y, group) === 0) ?? ys.reduce((a, b) => (clashes(b, group) < clashes(a, group) ? b : a))
  }

  const whole = candidates(samples[0].preferred).find((y) => clashes(y, samples) === 0)
  if (whole !== undefined) return [{ startSec, y: whole }]

  const groups: Array<typeof samples> = []
  for (const s of samples) {
    const last = groups[groups.length - 1]
    if (last && last[0].chunk === s.chunk) last.push(s)
    else groups.push([s])
  }
  const placements = groups.map((g, i) => ({ startSec: i === 0 ? startSec : g[0].t, y: best(g) }))
  return placements.filter((p, i) => i === 0 || p.y !== placements[i - 1].y)
}

/** The box's centre at `t` along `placements`, easing between spots. */
export function placementYAt(placements: Placement[], t: number): number {
  let y = placements[0]?.y ?? DEFAULT_TEXT_Y
  for (let i = 1; i < placements.length; i++) {
    const p = placements[i]
    if (t < p.startSec) break
    const k = Math.min(1, (t - p.startSec) / PLACEMENT_EASE_SEC)
    y = y + (p.y - y) * (k * k * (3 - 2 * k))
  }
  return y
}

/** A rough height (fraction of the frame) for a text box of `text` wrapped at boxWidthPx. */
export function estimateTextBoxHeight(text: string, fontSizePx: number, boxWidthPx: number, paddingPx: number, frameHeightPx: number): number {
  const perLine = Math.max(1, Math.floor((boxWidthPx - 2 * paddingPx) / (fontSizePx * 0.62)))
  const lines = Math.max(1, Math.ceil(text.length / perLine))
  return (lines * fontSizePx * 1.2 + 2 * paddingPx) / frameHeightPx
}

/** Half the height captions take around their centre (caption-layout's band), as a fraction of the frame. */
const CAPTION_HALF_HEIGHT = 0.07

/** Where the captions sit over the clip, layout span by layout span (CaptionTrack's resolveCaptionCenter). */
export function captionBands(style: CaptionStyleConfig, meta: SceneMetadata | undefined, fps: number, totalFrames: number): TimedBand[] {
  const band = (chunk: SceneChunk | null, startSec: number, endSec: number) => {
    const { y } = resolveCaptionCenter(style, style.position === "auto" ? chunk : null)
    return { startSec, endSec, y0: y - CAPTION_HALF_HEIGHT, y1: y + CAPTION_HALF_HEIGHT }
  }
  if (!meta?.chunks?.length) return [band(null, 0, totalFrames / fps)]
  return resolveLayoutSpans(meta, fps, totalFrames).map((s) => band(s.chunk, s.startFrame / fps, s.endFrame / fps))
}

/** Share of the clip the card thumbnail is taken at (clip_generation.py: max(0.5, duration * 0.3)). */
const THUMBNAIL_AT_SHARE = 0.3

/**
 * A thumbnail framed on one face (preview.py face_crop) has it FACE_PANEL_Y down at under a third of
 * the height, or is a fixed crop around the first face: the box goes below the chin, above the
 * card's caption.
 */
const ONE_FACE_STILL_Y = 0.68

/**
 * Where a clip card's hook (a box `boxHeight` of the card tall) goes over the card's thumbnail, framed
 * as preview.py still_framing frames it: a single scene as in the video; otherwise the faces detected
 * on the thumbnail's frame (`stills`), two of them stacked like a split (hook on the seam).
 */
export function cardHookY(meta: (SceneMetadata & { stills?: Array<{ at: number; faces: SceneFace[] }> }) | undefined, durationSec: number, boxHeight: number): number {
  if (!meta?.chunks?.length) return DEFAULT_TEXT_Y
  const fps = meta.source.fps || 30
  const totalFrames = Math.max(1, Math.round(durationSec * fps))
  const at = Math.max(0.5, durationSec * THUMBNAIL_AT_SHARE)
  const place = (m: SceneMetadata) => placeTextBox({ meta: m, fps, totalFrames, width: 1080, height: 1920, startSec: at, endSec: at + 0.01, boxHeight })[0].y
  const chunk = meta.chunks.find((c) => c.start_sec <= at && at < c.end_sec) ?? meta.chunks[meta.chunks.length - 1]
  if (chunk.mode === "single" && chunk.face_center_x_pct !== undefined) return place(meta)
  const still = meta.stills?.find((s) => Math.abs(s.at - at) < 0.05)
  const faces = (still?.faces ?? chunk.faces ?? []).map((f) => faceAt(f, at))
  if (faces.length < 2) return ONE_FACE_STILL_Y
  const pair = [...faces].sort((a, b) => a.w_pct * a.h_pct - b.w_pct * b.h_pct).slice(-2).sort((a, b) => a.x_pct - b.x_pct)
  return place({ ...meta, layout: undefined, chunks: [{ id: "thumbnail", start_sec: 0, end_sec: durationSec, mode: "dual", face_count: 2, faces: pair }] })
}

/** Every face band (with hair and chin margins) over the clip, sampled each `stepSec`, for overlays to avoid. */
export function faceOverlayBands(meta: SceneMetadata | undefined, fps: number, totalFrames: number, width: number, height: number, stepSec = 0.5): TimedBand[] {
  if (!meta?.chunks?.length) return []
  const spans = resolveLayoutSpans(meta, fps, totalFrames)
  const bands: TimedBand[] = []
  for (let t = 0; t < totalFrames / fps; t += stepSec) {
    const chunk = layoutChunkAt(spans, Math.round(t * fps))
    if (!chunk) continue
    for (const b of faceBandsAt(chunk, t + stepSec / 2, width, height, meta.source)) bands.push({ ...b, startSec: t, endSec: t + stepSec })
  }
  return bands
}
